import { type FC } from 'react'

import fileAi from '@core/assets/v2/icons/file-formats/file-ai.svg'
import fileArchive from '@core/assets/v2/icons/file-formats/file-archive.svg'
import fileAudio from '@core/assets/v2/icons/file-formats/file-audio.svg'
import fileBinary from '@core/assets/v2/icons/file-formats/file-binary.svg'
import fileCalendar from '@core/assets/v2/icons/file-formats/file-calendar.svg'
import fileCode from '@core/assets/v2/icons/file-formats/file-code.svg'
import fileDocument from '@core/assets/v2/icons/file-formats/file-document.svg'
import fileEmail from '@core/assets/v2/icons/file-formats/file-email.svg'
import fileEmptyFolder from '@core/assets/v2/icons/file-formats/file-empty-folder.svg'
import fileError from '@core/assets/v2/icons/file-formats/file-error.svg'
import fileExcel from '@core/assets/v2/icons/file-formats/file-excel.svg'
import fileFile from '@core/assets/v2/icons/file-formats/file-file.svg'
import fileFilledFolder from '@core/assets/v2/icons/file-formats/file-filled-folder.svg'
import fileForm from '@core/assets/v2/icons/file-formats/file-form.svg'
import fileGif from '@core/assets/v2/icons/file-formats/file-gif.svg'
import fileKey from '@core/assets/v2/icons/file-formats/file-key.svg'
import fileLink from '@core/assets/v2/icons/file-formats/file-link.svg'
import fileNumbers from '@core/assets/v2/icons/file-formats/file-numbers.svg'
import filePages from '@core/assets/v2/icons/file-formats/file-pages.svg'
import filePdf from '@core/assets/v2/icons/file-formats/file-pdf.svg'
import filePowerpoint from '@core/assets/v2/icons/file-formats/file-powerpoint.svg'
import filePsd from '@core/assets/v2/icons/file-formats/file-psd.svg'
import fileRaster from '@core/assets/v2/icons/file-formats/file-raster.svg'
import fileRecman from '@core/assets/v2/icons/file-formats/file-recman.svg'
import fileSpreadsheet from '@core/assets/v2/icons/file-formats/file-spreadsheet.svg'
import fileUnsupported from '@core/assets/v2/icons/file-formats/file-unsupported.svg'
import fileVector from '@core/assets/v2/icons/file-formats/file-vector.svg'
import fileVideo from '@core/assets/v2/icons/file-formats/file-video.svg'
import fileWord from '@core/assets/v2/icons/file-formats/file-word.svg'

import { createIcon } from './createIcon'
import type { FileFormatIconName, IconProps } from './types'

export const IconFileAi = createIcon(fileAi, 'FileAi')
export const IconFileArchive = createIcon(fileArchive, 'FileArchive')
export const IconFileAudio = createIcon(fileAudio, 'FileAudio')
export const IconFileBinary = createIcon(fileBinary, 'FileBinary')
export const IconFileCalendar = createIcon(fileCalendar, 'FileCalendar')
export const IconFileCode = createIcon(fileCode, 'FileCode')
export const IconFileDocument = createIcon(fileDocument, 'FileDocument')
export const IconFileEmail = createIcon(fileEmail, 'FileEmail')
export const IconFileEmptyFolder = createIcon(fileEmptyFolder, 'FileEmptyFolder')
export const IconFileError = createIcon(fileError, 'FileError')
export const IconFileExcel = createIcon(fileExcel, 'FileExcel')
export const IconFileFile = createIcon(fileFile, 'FileFile')
export const IconFileFilledFolder = createIcon(fileFilledFolder, 'FileFilledFolder')
export const IconFileForm = createIcon(fileForm, 'FileForm')
export const IconFileGif = createIcon(fileGif, 'FileGif')
export const IconFileKey = createIcon(fileKey, 'FileKey')
export const IconFileLink = createIcon(fileLink, 'FileLink')
export const IconFileNumbers = createIcon(fileNumbers, 'FileNumbers')
export const IconFilePages = createIcon(filePages, 'FilePages')
export const IconFilePdf = createIcon(filePdf, 'FilePdf')
export const IconFilePowerpoint = createIcon(filePowerpoint, 'FilePowerpoint')
export const IconFilePsd = createIcon(filePsd, 'FilePsd')
export const IconFileRaster = createIcon(fileRaster, 'FileRaster')
export const IconFileRecman = createIcon(fileRecman, 'FileRecman')
export const IconFileSpreadsheet = createIcon(fileSpreadsheet, 'FileSpreadsheet')
export const IconFileUnsupported = createIcon(fileUnsupported, 'FileUnsupported')
export const IconFileVector = createIcon(fileVector, 'FileVector')
export const IconFileVideo = createIcon(fileVideo, 'FileVideo')
export const IconFileWord = createIcon(fileWord, 'FileWord')

export const fileFormatIcons: Record<FileFormatIconName, FC<IconProps>> = {
    FileAi: IconFileAi,
    FileArchive: IconFileArchive,
    FileAudio: IconFileAudio,
    FileBinary: IconFileBinary,
    FileCalendar: IconFileCalendar,
    FileCode: IconFileCode,
    FileDocument: IconFileDocument,
    FileEmail: IconFileEmail,
    FileEmptyFolder: IconFileEmptyFolder,
    FileError: IconFileError,
    FileExcel: IconFileExcel,
    FileFile: IconFileFile,
    FileFilledFolder: IconFileFilledFolder,
    FileForm: IconFileForm,
    FileGif: IconFileGif,
    FileKey: IconFileKey,
    FileLink: IconFileLink,
    FileNumbers: IconFileNumbers,
    FilePages: IconFilePages,
    FilePdf: IconFilePdf,
    FilePowerpoint: IconFilePowerpoint,
    FilePsd: IconFilePsd,
    FileRaster: IconFileRaster,
    FileRecman: IconFileRecman,
    FileSpreadsheet: IconFileSpreadsheet,
    FileUnsupported: IconFileUnsupported,
    FileVector: IconFileVector,
    FileVideo: IconFileVideo,
    FileWord: IconFileWord,
}

const ICONS_BY_EXT: Record<string, FC<IconProps>> = {}

const mapExts = (IconComponent: FC<IconProps>, exts: string[]) => {
    exts.forEach((ext) => {
        ICONS_BY_EXT[ext] = IconComponent
    })
}

mapExts(IconFilePdf, ['pdf'])
mapExts(IconFileWord, ['doc', 'docx', 'docm', 'dotx'])
mapExts(IconFileExcel, ['xls', 'xlsx', 'xlsm', 'xltx'])
mapExts(IconFilePowerpoint, ['ppt', 'pptx', 'pptm', 'pps', 'ppsx', 'ppsm', 'pot', 'potx'])
mapExts(IconFileArchive, ['zip', 'gzip', '7z', 'tar', 'tgz', 'cab', 'rar'])
mapExts(IconFileAudio, ['mp3', 'wav', 'wave', 'midi', 'ac3'])
mapExts(IconFileVideo, ['avi', 'mp4', 'mpeg', 'fly', 'mpg', 'mkv', '3gp', 'mov', 'wmv'])
mapExts(IconFileRaster, ['png', 'jpg', 'jpeg', 'tif', 'tiff', 'jfif', 'exif', 'bpm', 'ppm', 'pgm', 'pbm'])
mapExts(IconFileGif, ['gif'])
mapExts(IconFileCode, ['json', 'xml', 'html', 'htm'])
mapExts(IconFileDocument, ['txt', 'rtf', 'tex', 'odt', 'wpd', 'wps', 'wks'])
mapExts(IconFileSpreadsheet, ['csv', 'p'])
mapExts(IconFileNumbers, ['numbers'])
mapExts(IconFilePages, ['pages'])
mapExts(IconFileAi, ['ai'])
mapExts(IconFilePsd, ['psd'])
mapExts(IconFileVector, ['svg', 'eps'])
mapExts(IconFileEmail, ['eml', 'msg'])
mapExts(IconFileCalendar, ['ics'])
mapExts(IconFileRecman, ['recman'])
mapExts(IconFileBinary, ['exe', 'dat'])
mapExts(IconFileKey, ['key'])
mapExts(IconFileForm, ['nda'])
mapExts(IconFileLink, ['link'])
mapExts(IconFileFile, ['file'])

export const getFileFormatIcon = (fileName: string): FC<IconProps> => {
    const ext = fileName.split('.').pop()?.toLowerCase() || ''
    return ICONS_BY_EXT[ext] || IconFileUnsupported
}
